'use client';

/**
 * `<InboxPreviewCard>` — карточка строки сообщения в dropdown (REQ-INB-002, INB §3.2).
 *
 * Контракт inbox §C.8.3. Рендерится в `<InboxDropdown>`. Кликабельность:
 * - `resolveMessageUrl(message)` → URL: `<a href>` (внешние — `noopener noreferrer`,
 *   внутренние — same-tab) + `markRead` по клику.
 * - URL === null → `<button>` (раскрытие/подсветка, без перехода) + `markRead`.
 *
 * Цветовая система: фон карточки — прозрачный (на фоне dropdown `panelStrongColor`);
 * индикатор «непрочитано» слева — `starColor` (оранжевый #ff8f3d, parity спецзначка
 * «непрочитано» evolve-news-feed §B7); точка unread — `brandAccentStart` (брендовый
 * фиолетовый). Иконка типа — `toneSoftColor` по соответствию (не вводим новые тоны;
 * используем muted-цвет текста для единообразия).
 *
 * Безопасность (ARCH-SEC-INB-001): `resolveMessageUrl` уже применил `isSafeActionUrl`
 * allow-list → `href` гарантированно safe-схемы. Запрещён `dangerouslySetInnerHTML`;
 * URL присваивается React'ом как `href`-атрибут (auto-escape).
 *
 * a11y: `role="article"`, `aria-label` с указанием unread; непрочитанное = точка
 * слева + жирный subject + aria (не только цвет, INB §12.1).
 */

import { Anchor, Group, Stack, Text } from '@mantine/core';

import type { PSMessage } from '@/lib/api/types';
import { getMessageTypeMeta } from '@/lib/inbox/message-type-meta';
import { resolveMessageUrl } from '@/lib/inbox/reference-routes';
import { formatRelativeTime } from './time-format';

interface InboxPreviewCardProps {
  message: PSMessage;
  onActivate: (message: PSMessage) => void;
}

export function InboxPreviewCard({ message, onActivate }: InboxPreviewCardProps) {
  const meta = getMessageTypeMeta(message.message_type);
  const Icon = meta.icon;
  const url = resolveMessageUrl(message);
  const isUnread = !message.is_read;
  const isInternal = url !== null && url.startsWith('/');

  // Общий контент карточки.
  const content = (
    <Group
      gap="sm"
      align="flex-start"
      wrap="nowrap"
      style={{
        padding: '8px 12px',
        // Полоса «непрочитано» слева: starColor (оранжевый, parity спецзначка
        // «непрочитано» из evolve-news-feed §B7). Прочитано — прозрачная.
        borderLeft: isUnread
          ? '3px solid #ff8f3d'
          : '3px solid transparent',
        cursor: 'pointer',
        transition: 'background 0.2s ease',
      }}
    >
      <Icon
        size={18}
        style={{
          marginTop: 2,
          flexShrink: 0,
          // mutedTextColor parity иконкам sidebar/header.
          color: 'light-dark(#6e6790, #b7abd7)',
        }}
        aria-hidden
      />
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={isUnread ? 700 : 500} lineClamp={1}>
          {message.subject}
        </Text>
        {message.content ? (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {message.content}
          </Text>
        ) : null}
        <Text size="xs" c="dimmed">
          {formatRelativeTime(message.created_at)}
        </Text>
      </Stack>
      {isUnread ? (
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            // Брендовый фиолетовый (accent start) — точка «непрочитано».
            background: '#7b4dff',
            flexShrink: 0,
            marginTop: 6,
          }}
        />
      ) : null}
    </Group>
  );

  const ariaLabel = `${meta.label}: ${message.subject}${isUnread ? ', непрочитано' : ''}`;

  if (url !== null) {
    // Кликабельная карточка → переход + markRead.
    return (
      <Anchor
        component="a"
        href={url}
        underline="never"
        display="block"
        role="article"
        aria-label={ariaLabel}
        rel={isInternal ? undefined : 'noopener noreferrer'}
        target={isInternal ? undefined : '_blank'}
        onClick={() => onActivate(message)}
        style={{ color: 'inherit' }}
      >
        {content}
      </Anchor>
    );
  }

  // Нет URL → button (раскрытие/подсветка, без перехода; fallback §8.2).
  return (
    <button
      type="button"
      onClick={() => onActivate(message)}
      role="article"
      aria-label={ariaLabel}
      style={{
        display: 'block',
        width: '100%',
        background: 'none',
        border: 'none',
        padding: 0,
        textAlign: 'left',
        cursor: 'pointer',
        color: 'inherit',
      }}
    >
      {content}
    </button>
  );
}
